import { DashboardLayout } from '@/layouts/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Users, Shield, BarChart3, AlertCircle, CheckCircle2 } from 'lucide-react';

export default function HRAdminDashboard() {
  const stats = [
    { label: 'Total Users', value: '142', icon: Users },
    { label: 'Active Jobs', value: '18', icon: BarChart3 },
    { label: 'Compliance Score', value: '98%', icon: Shield },
    { label: 'System Health', value: '100%', icon: CheckCircle2 },
  ];

  const complianceAlerts = [
    { type: 'success', title: 'Bias Audit Passed', message: 'Weekly bias audit completed. No disparate impact detected across all hiring decisions.' },
    { type: 'warning', title: 'Documentation Required', message: '3 job postings require bias audit documentation before going live.' },
    { type: 'info', title: 'Candidate Notifications', message: '12 candidates awaiting AI decision explanations. Auto-send scheduled for tomorrow.' },
  ];

  const userActivity = [
    { department: 'Engineering', users: 45, activeJobs: 8, applications: 234 },
    { department: 'Product', users: 28, activeJobs: 4, applications: 128 },
    { department: 'Design', users: 18, activeJobs: 3, applications: 84 },
    { department: 'Marketing', users: 22, activeJobs: 2, applications: 56 },
  ];

  const biasMetrics = [
    { metric: 'Gender Parity', score: 98, status: 'Excellent' },
    { metric: 'Ethnic Diversity', score: 95, status: 'Excellent' },
    { metric: 'Age Distribution', score: 92, status: 'Good' },
  ];

  const systemMetrics = [
    { name: 'API Uptime', value: '99.9%', status: 'healthy' },
    { name: 'Avg Response Time', value: '245ms', status: 'healthy' },
    { name: 'AI Processing', value: '1.2s', status: 'healthy' },
    { name: 'Storage Used', value: '42%', status: 'healthy' },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">HR Admin Dashboard</h1>
          <p className="text-gray-600 mt-2">Manage users, ensure compliance, and monitor system health</p>
        </div>

        {/* Stats */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat) => (
            <Card key={stat.label}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{stat.label}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
                </div>
                <div className="rounded-lg bg-primary-100 p-3 text-primary-600">
                  <stat.icon className="h-6 w-6" />
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Compliance Alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Shield className="h-5 w-5 mr-2 text-primary-600" />
              Compliance & Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {complianceAlerts.map((alert, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border ${
                    alert.type === 'success'
                      ? 'bg-green-50 border-green-200'
                      : alert.type === 'warning'
                      ? 'bg-yellow-50 border-yellow-200'
                      : 'bg-blue-50 border-blue-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      {alert.type === 'success' ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                      ) : alert.type === 'warning' ? (
                        <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                      )}
                      <div>
                        <h6 className={`font-semibold mb-1 ${
                          alert.type === 'success'
                            ? 'text-green-900'
                            : alert.type === 'warning'
                            ? 'text-yellow-900'
                            : 'text-blue-900'
                        }`}>
                          {alert.title}
                        </h6>
                        <p className={`text-sm ${
                          alert.type === 'success'
                            ? 'text-green-800'
                            : alert.type === 'warning'
                            ? 'text-yellow-800'
                            : 'text-blue-800'
                        }`}>
                          {alert.message}
                        </p>
                      </div>
                    </div>
                    {alert.type === 'warning' && (
                      <Button size="sm" variant="outline">Review</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Department Activity */}
          <Card>
            <CardHeader>
              <CardTitle>Department Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {userActivity.map((dept) => (
                  <div key={dept.department} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="font-semibold text-gray-900">{dept.department}</h5>
                      <Badge variant="info">{dept.users} users</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Active Jobs</p>
                        <p className="text-lg font-semibold text-gray-900">{dept.activeJobs}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Applications</p>
                        <p className="text-lg font-semibold text-primary-600">{dept.applications}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Bias Detection Metrics */}
          <Card>
            <CardHeader>
              <CardTitle>Bias Detection Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {biasMetrics.map((metric) => (
                  <div key={metric.metric} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">{metric.metric}</span>
                      <Badge variant="success">{metric.status}</Badge>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full" 
                          style={{ width: `${metric.score}%` }}
                        />
                      </div>
                      <span className="text-lg font-bold text-gray-900">{metric.score}%</span>
                    </div>
                  </div>
                ))}
                <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                  <h6 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <Shield className="h-5 w-5 mr-2 text-green-600" />
                    Compliance Status
                  </h6>
                  <p className="text-sm text-gray-700">
                    All hiring processes meet GDPR, EU AI Act, and NYC Local Law 144 requirements.
                    Next audit scheduled in 7 days.
                  </p>
                  <Button size="sm" variant="outline" className="mt-3">View Full Report</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* System Health */}
        <Card>
          <CardHeader>
            <CardTitle>System Health & Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-4">
              {systemMetrics.map((metric) => (
                <div key={metric.name} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-gray-500">{metric.name}</p>
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                  </div>
                  <p className="text-xl font-bold text-gray-900">{metric.value}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-semibold text-gray-900">Last System Check</p>
                <p className="text-sm text-gray-600">5 minutes ago • All systems operational</p>
              </div>
              <Button size="sm" variant="outline">View Logs</Button>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-3">
              <Button variant="outline" className="justify-start">
                <Users className="h-4 w-4 mr-2" />
                Manage Users
              </Button>
              <Button variant="outline" className="justify-start">
                <BarChart3 className="h-4 w-4 mr-2" />
                Export Analytics
              </Button>
              <Button variant="outline" className="justify-start">
                <Shield className="h-4 w-4 mr-2" />
                Run Bias Audit
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
